import { useEffect, useRef, useState } from "react";
import { X, Play, RotateCcw, Video, Square } from "lucide-react";
import { useReelRecorder } from "./useReelRecorder";

const IMG = "https://static.prod-images.emergentagent.com/jobs/5d950d92-6b67-4808-826a-961251c2afb8/images/";
const P = {
  exterior: IMG + "d6a5faac6fcd422c0e18f3fbc9658e7817e27a8d237a85134a5514ed52715dd1.png",
  foyer: IMG + "082e788885acaa524c5db0f437d4ed782484a68109a6b86b5c2f0d8bbcde390f.png",
  kitchen: IMG + "21779fc58039bbc32978b5f56b82dc9a714075c001a23a6d350380b70fb3c91c.png",
  vignette: IMG + "098bbfc97f9559ceb3c9d3a08b3ca327aa54bcae6a4239026af3d150c4c7f09a.png",
  living: IMG + "d1452cbca255de4ff974e4e1215d25a49907c885d767e108c6670daccf3a149c.png",
  bedroom: IMG + "3465e4cd62bbfe714867577b4ad19221896c66e5e808b36960fffffbee2b65de.png",
  dining: IMG + "90aae460a8a8ca020be559c930014549f27c4a61c6d17399177cf9d35401b2c6.png",
  cream: IMG + "d1452cbca255de4ff974e4e1215d25a49907c885d767e108c6670daccf3a149c.png",
};

const FRAME_MS = 4200;

// each frame: kind (title|photo|spread|close) + content
const FRAMES = [
  { kind: "title", kicker: "For interior designers", title: "The New England Edit", sub: "A studio for classic, considered design." },
  { kind: "photo", src: P.exterior, kicker: "Classic · Coastal · Considered", caption: "" },
  { kind: "photo", src: P.foyer, kicker: "", caption: "Every room, thoughtfully composed." },
  { kind: "spread", spread: "mood", kicker: "Mood Boards", title: "Collect the vision" },
  { kind: "photo", src: P.living, kicker: "", caption: "From concept to install." },
  { kind: "spread", spread: "palette", kicker: "Paint Palettes", title: "A scheme, in one click" },
  { kind: "photo", src: P.kitchen, kicker: "In the details", caption: "" },
  { kind: "spread", spread: "dash", kicker: "Every detail, tracked", title: "Budgets · FF&E · Approvals" },
  { kind: "photo", src: P.bedroom, kicker: "", caption: "Beautifully managed, start to finish." },
  { kind: "close" },
];

const SPREAD_PHOTOS = [P.living, P.bedroom, P.foyer];
const SWATCHES = [["Chantilly Lace", "#F4F2EC"], ["White Sand", "#E8DECB"], ["Salted Lake", "#B7C4C6"], ["Newburg Green", "#3E5A5B"], ["Hale Navy", "#3B4A57"]];

function Frame({ f }) {
  if (f.kind === "title" || f.kind === "close") {
    const close = f.kind === "close";
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-10" style={{ background: "#F4F0EA" }}>
        <div className="animate-[slowRise_1.1s_ease]">
          <p className="text-[12px] uppercase tracking-[0.45em] text-[#A68A6B] mb-6">{close ? "Design, managed beautifully" : (f.kicker || "")}</p>
          <div className="w-16 h-px bg-[#C9BBAE] mx-auto mb-6" />
          <h1 className="font-serif text-[#2B2622] tracking-tight" style={{ fontSize: close ? "4.5rem" : "4rem", lineHeight: 1.02 }}>
            {close ? "WorkRoom" : f.title}
          </h1>
          {close ? (
            <p className="text-[#5C5651] mt-6 uppercase tracking-[0.35em] text-sm">workroomadmin.com</p>
          ) : (
            <p className="text-[#5C5651] mt-6 font-serif italic text-xl">{f.sub}</p>
          )}
        </div>
      </div>
    );
  }
  if (f.kind === "photo") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-[#1F1B17]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${f.src})`, animation: `kenburns ${FRAME_MS + 900}ms ease-out both` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />
        {f.kicker && (
          <div className="absolute top-10 left-10 animate-[fadeIn_1.2s_ease]">
            <p className="text-white/95 uppercase tracking-[0.4em] text-sm" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.4)" }}>{f.kicker}</p>
            <div className="w-14 h-px bg-white/70 mt-3" />
          </div>
        )}
        {f.caption && (
          <div className="absolute bottom-12 left-10 right-10 animate-[fadeUp_1.2s_ease]">
            <p className="text-white font-serif italic" style={{ fontSize: "2rem", textShadow: "0 1px 10px rgba(0,0,0,0.45)" }}>{f.caption}</p>
          </div>
        )}
      </div>
    );
  }
  // editorial app spread
  return (
    <div className="absolute inset-0 flex" style={{ background: "#F4F0EA" }}>
      <div className="w-1/2 flex flex-col justify-center pl-16 pr-8 animate-[slowRise_1s_ease]">
        <p className="text-[12px] uppercase tracking-[0.4em] text-[#A68A6B] mb-4">{f.kicker}</p>
        <div className="w-12 h-px bg-[#C9BBAE] mb-4" />
        <h2 className="font-serif text-[#2B2622]" style={{ fontSize: "3rem", lineHeight: 1.05 }}>{f.title}</h2>
        <p className="text-[#5C5651] mt-4 text-sm uppercase tracking-widest">WorkRoom</p>
      </div>
      <div className="w-1/2 flex items-center justify-center pr-16 pl-8">
        {f.spread === "mood" && (
          <div className="grid grid-cols-2 gap-3 w-full max-w-sm animate-[fadeIn_1.1s_ease]">
            {SPREAD_PHOTOS.concat([P.kitchen]).map((src, i) => (
              <div key={i} className="h-40 bg-cover bg-center shadow-sm" style={{ backgroundImage: `url(${src})` }} />
            ))}
          </div>
        )}
        {f.spread === "palette" && (
          <div className="w-full max-w-sm bg-white shadow-sm animate-[fadeIn_1.1s_ease]">
            {SWATCHES.map((s, i) => (
              <div key={i} className="flex items-center">
                <div className="h-14 w-20 shrink-0" style={{ background: s[1] }} />
                <span className="px-4 text-sm text-[#2B2622] font-serif">{s[0]}</span>
              </div>
            ))}
          </div>
        )}
        {f.spread === "dash" && (
          <div className="w-full max-w-sm bg-white border border-[#EBE5E0] shadow-sm animate-[fadeIn_1.1s_ease]">
            <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${P.exterior})` }} />
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#A68A6B]">Procurement</p>
              <h3 className="font-serif text-xl text-[#2B2622]">New Canaan</h3>
              <p className="text-xs text-[#9b938b] mb-3">Coastal Remodel</p>
              {[["Budget", "$87,500"], ["FF&E items", "24"], ["Approvals", "6 pending"]].map(([k, v], i) => (
                <div key={i} className="flex justify-between py-1.5 border-b border-[#F2EFEB] text-sm">
                  <span className="text-[#9b938b] uppercase tracking-wider text-[10px]">{k}</span>
                  <span className="text-[#2B2622]">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MagazineReel({ onClose }) {
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const timer = useRef(null);
  const { recording, start: recStart, stop: recStop } = useReelRecorder("workroom-lookbook");
  const recMode = useRef(false);

  useEffect(() => {
    if (!started) return;
    if (idx >= FRAMES.length) {
      if (recMode.current) { recMode.current = false; recStop(); setStarted(false); setIdx(0); }
      else onClose();
      return;
    }
    timer.current = setTimeout(() => setIdx((p) => p + 1), FRAME_MS);
    return () => clearTimeout(timer.current);
  }, [idx, started]); // eslint-disable-line

  const start = () => {
    setStarted(true);
    setIdx(0);
  };
  const recordAndPlay = async () => {
    const ok = await recStart();
    if (!ok) return;
    recMode.current = true;
    setStarted(true);
    setIdx(0);
  };
  const stopRecording = () => { recMode.current = false; recStop(); setStarted(false); setIdx(0); };
  const restart = () => { clearTimeout(timer.current); setIdx(0); setStarted(true); };

  const f = FRAMES[Math.min(idx, FRAMES.length - 1)];

  return (
    <div className="fixed inset-0 z-[100] bg-black/92 flex items-center justify-center p-4" data-testid="mag-reel">
      <button data-testid="mag-close" onClick={onClose} className="absolute top-5 right-6 text-white/70 hover:text-white z-20"><X size={26} /></button>
      <div className="w-full max-w-6xl">
        <div className="relative w-full aspect-video overflow-hidden shadow-2xl bg-[#F4F0EA]" data-testid="mag-frame">
          {!started ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ background: "#F4F0EA" }}>
              <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${P.exterior})` }} />
              <div className="relative">
                <p className="text-[12px] uppercase tracking-[0.45em] text-[#A68A6B] mb-4">Editorial Lookbook · 16:9</p>
                <h2 className="font-serif text-5xl text-[#2B2622]">The New England Edit</h2>
                <button data-testid="mag-play" onClick={start}
                  className="mt-7 inline-flex items-center gap-2 bg-[#2B2622] hover:bg-[#A68A6B] text-white uppercase tracking-wider text-sm px-8 py-4 transition-colors">
                  <Play size={18} /> Play lookbook
                </button>
                <button data-testid="mag-record" onClick={recordAndPlay}
                  className="mt-3 ml-3 inline-flex items-center gap-2 border border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-sm px-6 py-4 transition-colors">
                  <Video size={18} /> Record & download
                </button>
                <p className="text-[11px] text-[#8C7154] mt-4">Play to screen-record, or "Record & download" to save a video (choose this tab when prompted). ~40 seconds.</p>
              </div>
            </div>
          ) : (
            <>
              <div key={idx} className="absolute inset-0"><Frame f={f} /></div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10">
                <div className="h-full bg-[#A68A6B] transition-all duration-300" style={{ width: `${((idx + 1) / FRAMES.length) * 100}%` }} />
              </div>
            </>
          )}
        </div>
        {started && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button data-testid="mag-restart" onClick={restart} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-wider"><RotateCcw size={15} /> Restart</button>
            {recording && (
              <button data-testid="mag-stop-record" onClick={stopRecording} className="inline-flex items-center gap-2 text-[#E7A0A0] hover:text-white text-xs uppercase tracking-wider">
                <Square size={13} className="fill-current" /> Stop & save
              </button>
            )}
            <span className="text-white/40 text-xs">{Math.min(idx + 1, FRAMES.length)} / {FRAMES.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}
